import { FlowsErrorCodes, type TFlowsErrorCode } from '../constants/flows.error-codes';
import { ENodeKind, ENodeType, ERROR_PORT, FLOW_LIMITS } from '../constants/flow.constants';
import { continueOnFailOf, nodeTypeOf, type INodeRefSpec } from '../registry/node-types.registry';
import {
  FlowGraphSchema,
  type TFlowEdge,
  type TFlowGraph,
  type TFlowNode,
} from '../schemas/flow-graph.schema';
import {
  ancestors,
  buildDominance,
  buildGraphIndex,
  countPaths,
  cycleEdges,
  descendants,
  edgeId,
  enclosingLoops,
  fanoutPorts,
  inputsCanCoOccur,
  joinInputReach,
  legacyEdgeOutput,
  mergeWaitsForAll,
  normalizeOnErrorEdges,
  pairedSplit,
  ROOT_SCOPE,
  scopeEntries,
  scopeExits,
  scopeOf,
  splitBrackets,
  topoOrder,
  type IDominance,
  type IGraphIndex,
} from './flow-graph.analysis';
import { agentFindings, type IAgentFinding } from '../agents/agent-validator';
import { copyLifetime, type ILifetimeOptions } from './flow-graph.compiler';
import {
  canonicalKeyPath,
  collectTemplateRefs,
  parseContextPath,
  type IContextPath,
} from './flow-template';
import { fallbackLabel, fieldLabel, plainProblem } from './flow-issue-words';

export type TFlowIssueSeverity = 'error' | 'warning';

/** One finding, pinned to where the canvas should point ("2 things to fix before publishing"). */
export interface IFlowIssue {
  code: TFlowsErrorCode;
  message: string;
  severity: TFlowIssueSeverity;
  nodeKey?: string;
  edge?: Pick<TFlowEdge, 'from' | 'output' | 'to' | 'input'>;
  field?: string;
  details?: Record<string, unknown>;
}

export interface IFlowValidation {
  ok: boolean;
  graph: TFlowGraph | null;
  issues: IFlowIssue[];
}

export type TFlowRef = INodeRefSpec & { nodeKey: string };

export interface IValidateOptions {
  /** Answers which refs the organisation does not have; the API loads them by `flowRefs`. */
  resolveRefs?: (refs: readonly TFlowRef[]) => readonly TFlowRef[];
  /**
   * A connector action's own input schema lives in the API (§6.1): the caller checks
   * `action.input` against it and answers with field paths (`input.channel`).
   */
  checkActionInput?: (node: TFlowNode) => readonly { field: string; message: string }[];
  /** Literal addresses the outbound policy refuses; looked up by the API (DNS) beforehand. */
  outboundRefusals?: readonly IOutboundRefusal[];
  /** Code steps whose `source` the sandbox would not load; compiled by the API beforehand. */
  codeFindings?: readonly ICodeFinding[];
  /** AI steps' Library prompt variables left unbound (or bound but unknown), §16. */
  promptFindings?: readonly IPromptFinding[];
  /** Pinned code version id → the handler it was written for (§16); the API loads them. */
  codeSignatures?: ReadonlyMap<string, 'event' | 'request'>;
  /** Names of steps no longer in the graph (the live version's): "uses a step that was removed". */
  formerNames?: ReadonlyMap<string, string>;
  /** A connector action's own label for an `input.*` path; null when it has none. */
  inputLabel?: (node: TFlowNode, field: string) => string | null;
  /** Agent rules that read the workspace: the account's app, prices, Run a flow's target. */
  agentFindings?: readonly IAgentFinding[];
}

export interface IPromptFinding {
  nodeKey: string;
  /** `library.vars.<name>`. */
  field: string;
  /** Unbound blocks publishing; a binding the version does not read only warns. */
  unbound: boolean;
  message: string;
}

export interface ICodeFinding {
  nodeKey: string;
  message: string;
  /** 1-based, in the step's `source`, when the runtime could tell. */
  line: number | null;
  column: number | null;
  code: string;
}

export interface IOutboundRefusal {
  nodeKey: string;
  field: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface IPublishOptions extends ILifetimeOptions, IValidateOptions {
  /** `flows.retention_days`; when given, a copy must not outlive it (§9.5). */
  retentionDays?: number;
  /** Nodes of the live version that saw no traffic in 7 days — stats, supplied by the caller. */
  silentNodeKeys?: readonly string[];
}

const C = FlowsErrorCodes;

/** Saving a draft: what makes the graph unreadable; the rest is a warning until publish. */
export function validateDraft(raw: unknown, options: IValidateOptions = {}): IFlowValidation {
  return run(raw, false, options);
}

export function validateForPublish(raw: unknown, options: IPublishOptions = {}): IFlowValidation {
  return run(raw, true, options);
}

export function flowRefs(graph: TFlowGraph): TFlowRef[] {
  const index = buildGraphIndex(graph);
  return [...index.nodes].flatMap(([key, node]) =>
    (index.descriptors.get(key)?.refs(node.config ?? {}) ?? []).map((ref) => ({
      ...ref,
      nodeKey: key,
    })),
  );
}

// A node's `config` is arbitrary JSON (§1): nothing stops a caller from nesting it hundreds of
// levels deep, which would blow the stack in `FlowGraphSchema.safeParse` or the graph walk below
// before any of that machinery gets a chance to report a tidy issue. Checked first, iteratively.
const MAX_GRAPH_DEPTH = 64;

function tooDeep(value: unknown, limit: number): boolean {
  let stack: unknown[] = [value];
  for (let depth = 0; stack.length > 0; depth++) {
    if (depth > limit) return true;
    const next: unknown[] = [];
    for (const item of stack) {
      if (Array.isArray(item)) next.push(...item);
      else if (item && typeof item === 'object') next.push(...Object.values(item));
    }
    stack = next;
  }
  return false;
}

function run(raw: unknown, strict: boolean, options: IPublishOptions): IFlowValidation {
  if (tooDeep(raw, MAX_GRAPH_DEPTH)) {
    const issues = new Collector({ schema: 1, nodes: [], edges: [] });
    issues.error(C.INVALID_GRAPH, `A flow graph may be nested at most ${MAX_GRAPH_DEPTH} levels`);
    return { ok: false, graph: null, issues: issues.finish() };
  }
  const parsed = FlowGraphSchema.safeParse(raw);
  if (!parsed.success) {
    const issues = new Collector({ schema: 1, nodes: [], edges: [] });
    for (const issue of parsed.error.issues) {
      issues.error(C.GRAPH_SHAPE_INVALID, issue.message, { field: issue.path.join('.') });
    }
    return { ok: false, graph: null, issues: issues.finish() };
  }
  const graph = canonicalDedupePaths(normalizeOnErrorEdges(parsed.data));
  const issues = new Collector(graph, options);
  const index = buildGraphIndex(graph);
  structural(index, issues);
  if (strict) publishRules(index, issues, options);
  else {
    // A new flow is saved before its start is chosen (on the canvas); only publish refuses.
    if (!index.trigger) issues.warn(C.TRIGGER_MISSING, 'Choose what starts this flow');
    references(index, buildDominance(index), issues, false);
  }
  refsExist(index, issues, options, strict);
  actionInputs(index, issues, options, strict);
  outbound(index, issues, options, strict);
  codeFindings(index, issues, options, strict);
  codeSignatures(index, issues, options, strict);
  promptFindings(index, issues, options, strict);
  agentRules(index, issues, options, strict);
  const list = issues.finish();
  return { ok: !list.some((issue) => issue.severity === 'error'), graph, issues: list };
}

// Drafts and versions store this graph, so old Dedupe spellings are saved in the one grammar.
function canonicalDedupePaths(graph: TFlowGraph): TFlowGraph {
  let changed = false;
  const nodes = graph.nodes.map((node) => {
    const paths = node.config?.keyPaths;
    if (node.type !== ENodeType.DEDUPE || !Array.isArray(paths)) return node;
    const next = paths.map((path) => (typeof path === 'string' ? canonicalKeyPath(path) : path));
    if (next.every((path, i) => path === paths[i])) return node;
    changed = true;
    return { ...node, config: { ...node.config, keyPaths: next } };
  });
  return changed ? { ...graph, nodes } : graph;
}

type TIssueAt = Omit<IFlowIssue, 'code' | 'message' | 'severity'>;

class Collector {
  private readonly list: IFlowIssue[] = [];
  private readonly names = new Map<string, string>();
  private readonly nodes = new Map<string, TFlowNode>();

  constructor(
    graph: TFlowGraph,
    private readonly options: IValidateOptions = {},
  ) {
    for (const node of graph.nodes) {
      this.names.set(node.key, node.name);
      this.nodes.set(node.key, node);
    }
  }

  name(key: string): string {
    return this.names.get(key) ?? this.options.formerNames?.get(key) ?? key;
  }

  /** In the live version but not here any more — as against a key no version ever had. */
  wasRemoved(key: string): boolean {
    return this.options.formerNames?.has(key) ?? false;
  }

  /** A config path as the panel labels it — `“Message”`, never `input.text` (§5.1). */
  field(nodeKey: string, field: string): string {
    const node = this.nodes.get(nodeKey);
    const descriptor = node ? nodeTypeOf(node.type) : undefined;
    // An action's `input.*` is labelled by its connector, not by the generic "Input" record.
    const label =
      (node ? (this.options.inputLabel?.(node, field) ?? null) : null) ??
      (descriptor ? fieldLabel(descriptor.fields, field) : null) ??
      fallbackLabel(field);
    return `“${label.charAt(0).toUpperCase()}${label.slice(1)}”`;
  }

  error(code: TFlowsErrorCode, message: string, at: TIssueAt = {}): void {
    this.list.push({ code, message, severity: 'error', ...at });
  }

  warn(code: TFlowsErrorCode, message: string, at: TIssueAt = {}): void {
    this.list.push({ code, message, severity: 'warning', ...at });
  }

  issue(strict: boolean, code: TFlowsErrorCode, message: string, at: TIssueAt): void {
    if (strict) this.error(code, message, at);
    else this.warn(code, message, at);
  }

  /** Errors first, at most `maxIssues`: a broken 200-step graph must not answer with 60k rows. */
  finish(): IFlowIssue[] {
    const sorted = [
      ...this.list.filter((issue) => issue.severity === 'error'),
      ...this.list.filter((issue) => issue.severity === 'warning'),
    ];
    if (sorted.length <= FLOW_LIMITS.maxIssues) return sorted;
    const kept = sorted.slice(0, FLOW_LIMITS.maxIssues - 1);
    const rest = sorted.slice(kept.length);
    const errors = rest.filter((issue) => issue.severity === 'error').length;
    kept.push({
      code: C.TOO_MANY_ISSUES,
      severity: errors > 0 ? 'error' : 'warning',
      message: `…and ${rest.length} more (${errors} of them ${errors === 1 ? 'is an error' : 'are errors'}); fix these first`,
      details: { more: rest.length, errors },
    });
    return kept;
  }
}

function structural(index: IGraphIndex, issues: Collector): void {
  const { graph } = index;
  if (graph.nodes.length > FLOW_LIMITS.maxNodes) {
    issues.error(
      C.TOO_MANY_NODES,
      `${graph.nodes.length} steps; a flow holds at most ${FLOW_LIMITS.maxNodes}`,
    );
  }
  if (graph.edges.length > FLOW_LIMITS.maxEdges) {
    issues.error(
      C.TOO_MANY_EDGES,
      `${graph.edges.length} connections; at most ${FLOW_LIMITS.maxEdges}`,
    );
  }

  const seen = new Set<string>();
  for (const node of graph.nodes) {
    if (seen.has(node.key)) {
      issues.error(C.DUPLICATE_NODE_KEY, `Two steps use the key "${node.key}"`, {
        nodeKey: node.key,
      });
    }
    seen.add(node.key);
    if (!index.descriptors.get(node.key)) {
      issues.error(C.UNKNOWN_NODE_TYPE, `${node.name}: "${node.type}" is not a step type`, {
        nodeKey: node.key,
        field: 'type',
      });
    }
  }

  loopContainment(index, issues);
  triggers(index, issues);
  edges(index, issues);

  for (const edge of cycleEdges(index)) {
    issues.error(
      C.EDGE_CREATES_CYCLE,
      `Joining ${issues.name(edge.from)} back to ${issues.name(edge.to)} would send every event ` +
        'round forever. To repeat part of a flow, wrap it in a Loop — it has a condition and a limit.',
      { edge, nodeKey: edge.from },
    );
  }
}

function loopContainment(index: IGraphIndex, issues: Collector): void {
  const claimed = new Map<string, string>();
  for (const node of index.graph.nodes) {
    if (!node.children || node.children.length === 0) continue;
    if (node.type !== ENodeType.LOOP) {
      issues.error(
        C.LOOP_CHILDREN_ON_NON_LOOP,
        `${node.name} is not a Loop and cannot hold steps`,
        {
          nodeKey: node.key,
          field: 'children',
        },
      );
      continue;
    }
    for (const child of node.children) {
      const at = { nodeKey: node.key, field: 'children', details: { child } };
      if (!index.nodes.has(child)) {
        issues.error(
          C.LOOP_CHILD_UNKNOWN,
          `${node.name} lists "${child}", which is not in the flow`,
          at,
        );
      } else if (child === node.key || index.parentLoop.get(child) !== node.key) {
        const owner = claimed.get(child) ?? index.parentLoop.get(child);
        issues.error(
          C.LOOP_CHILD_SHARED,
          owner && owner !== node.key
            ? `${issues.name(child)} already repeats inside ${issues.name(owner)}; a step belongs to one loop`
            : `${node.name} cannot contain itself or a loop that contains it`,
          at,
        );
      }
      claimed.set(child, claimed.get(child) ?? node.key);
    }
  }
  for (const [key, node] of index.nodes) {
    if (node.type !== ENodeType.LOOP) continue;
    const depth = enclosingLoops(index, key).length + 1;
    if (depth > FLOW_LIMITS.maxLoopDepth && (index.scopeMembers.get(key) ?? []).length > 0) {
      issues.error(
        C.LOOP_NESTING_TOO_DEEP,
        `${node.name} is a loop inside a loop inside a loop; loops nest at most ${FLOW_LIMITS.maxLoopDepth} deep`,
        { nodeKey: key },
      );
    }
  }
}

function triggers(index: IGraphIndex, issues: Collector): void {
  const all = [...index.nodes.keys()].filter(
    (key) => index.descriptors.get(key)?.kind === ENodeKind.TRIGGER,
  );
  if (all.length > 1) {
    for (const key of all.slice(1)) {
      issues.error(
        C.TRIGGER_MULTIPLE,
        `A flow starts from one trigger; ${issues.name(key)} is a second`,
        {
          nodeKey: key,
        },
      );
    }
  }
  for (const key of all) {
    if (scopeOf(index, key) !== ROOT_SCOPE) {
      issues.error(C.TRIGGER_NOT_AT_ROOT, `${issues.name(key)} cannot sit inside a loop`, {
        nodeKey: key,
      });
    }
  }
  for (const edge of index.graph.edges) {
    if (all.includes(edge.to)) {
      issues.error(
        C.TRIGGER_HAS_INPUT,
        `Nothing can lead into the trigger ${issues.name(edge.to)}`,
        {
          edge,
          nodeKey: edge.to,
        },
      );
    }
  }
}

function edges(index: IGraphIndex, issues: Collector): void {
  const seen = new Set<string>();
  for (const edge of index.graph.edges) {
    const at = { edge, nodeKey: edge.from };
    const id = edgeId(edge);
    const duplicate = seen.has(id);
    seen.add(id);
    if (duplicate) issues.error(C.EDGE_DUPLICATE, 'The same connection is drawn twice', at);
    if (!index.nodes.has(edge.from) || !index.nodes.has(edge.to)) {
      const missing = index.nodes.has(edge.from) ? edge.to : edge.from;
      issues.error(
        C.EDGE_UNKNOWN_NODE,
        `A connection points at "${missing}", which is not in the flow`,
        at,
      );
      continue;
    }
    if (edge.from === edge.to) {
      issues.error(C.EDGE_TO_SELF, `${issues.name(edge.from)} cannot lead into itself`, at);
      continue;
    }

    const outputs = index.outputs.get(edge.from) ?? [];
    // Item 2 legacy alias: a wait node drawn before the port rename still reads "main".
    const output = legacyEdgeOutput(index.nodes.get(edge.from), edge.output);
    const continued = output === ERROR_PORT && continueOnFailOf(index.nodes.get(edge.from)!);
    if (continued) {
      // Kept, not dropped, when the setting changes: what the user drew never vanishes (§3.4).
      issues.error(
        C.EDGE_UNKNOWN_OUTPUT,
        `${issues.name(edge.from)} goes on when it fails, so it has no error output: remove the ` +
          `connection to ${issues.name(edge.to)}, or set "If this step fails" back to stop`,
        { ...at, details: { onError: 'continue' } },
      );
    } else if (index.descriptors.get(edge.from) && !outputs.some((spec) => spec.key === output)) {
      issues.error(
        C.EDGE_UNKNOWN_OUTPUT,
        `${issues.name(edge.from)} has no "${edge.output}" output (it has ${outputs.map((o) => o.key).join(', ') || 'none'})`,
        at,
      );
    }
    const inputs = index.inputs.get(edge.to) ?? [];
    const toKind = index.descriptors.get(edge.to)?.kind;
    if (toKind && toKind !== ENodeKind.TRIGGER && !inputs.includes(edge.input)) {
      issues.error(C.EDGE_UNKNOWN_INPUT, `${issues.name(edge.to)} has no input "${edge.input}"`, {
        ...at,
        nodeKey: edge.to,
      });
    }
    if (edge.handled === true && edge.output !== ERROR_PORT) {
      issues.error(C.EDGE_HANDLED_NOT_ERROR, 'Only an error output can be marked handled', at);
    }
    const fromScope = scopeOf(index, edge.from);
    const toScope = scopeOf(index, edge.to);
    if (fromScope !== toScope) {
      const loop = fromScope !== ROOT_SCOPE ? fromScope : toScope;
      issues.error(
        C.LOOP_EDGE_CROSSES_BOUNDARY,
        `The connection ${issues.name(edge.from)} → ${issues.name(edge.to)} crosses the frame of ` +
          `${issues.name(loop)}; the loop leads into its first step and out of its last one`,
        at,
      );
    }
  }
}

function publishRules(index: IGraphIndex, issues: Collector, options: IPublishOptions): void {
  const trigger = index.trigger;
  if (!trigger) {
    issues.error(
      C.TRIGGER_MISSING,
      'Choose what starts this flow before publishing it: a request on a source, a schedule, or a manual run',
    );
  } else if ((index.out.get(trigger) ?? []).length === 0) {
    issues.error(
      C.NOTHING_AFTER_TRIGGER,
      'Nothing to publish yet — a flow needs at least one step after its trigger',
      { nodeKey: trigger },
    );
  }

  reachability(index, issues);
  nodeConfigs(index, issues);
  disabledNodes(index, issues);
  loopBodies(index, issues);
  merges(index, issues);

  const dominance = buildDominance(index);
  placement(index, dominance, issues);
  references(index, dominance, issues, true);
  warnings(index, issues, options);

  if (options.retentionDays !== undefined && trigger) {
    const lifetime = copyLifetime(index.graph, options);
    const limit = options.retentionDays * 86_400;
    if (lifetime.seconds > limit) {
      // A warning, not a refusal (gap-closing §2): open copies are protected by the
      // retention archive anyway, so publishing with too-short retention just needs saying.
      issues.warn(
        C.RETENTION_BELOW_COPY_LIFETIME,
        `Keeps less than its retries take — raise to ${Math.ceil(lifetime.seconds / 86_400)} days`,
        { details: { lifetimeSec: lifetime.seconds, retentionSec: limit, path: lifetime.path } },
      );
    }
  }
}

const REF_WORDS: Record<INodeRefSpec['kind'], string> = {
  source: 'source',
  destination: 'destination',
  transform_version: 'code version',
  credential: 'account',
  flow: 'flow',
};

function refsExist(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  if (!options.resolveRefs) return;
  for (const ref of options.resolveRefs(flowRefs(index.graph))) {
    // Both Library kinds are `transform_version` refs; the field tells which the step wanted.
    const word = ref.field.startsWith('library.') ? 'prompt version' : REF_WORDS[ref.kind];
    issues.issue(
      strict,
      C.REF_NOT_FOUND,
      `${issues.name(ref.nodeKey)} · ${issues.field(ref.nodeKey, ref.field)}: there is no ${word} "${ref.id}" in this workspace`,
      { nodeKey: ref.nodeKey, field: ref.field, details: { kind: ref.kind, id: ref.id } },
    );
  }
}

function actionInputs(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  if (!options.checkActionInput) return;
  for (const node of index.nodes.values()) {
    if (node.type !== ENodeType.ACTION || node.disabled) continue;
    for (const found of options.checkActionInput(node)) {
      issues.issue(
        strict,
        C.ACTION_INPUT_INVALID,
        // The connector layer words it against the action's field labels (flow-issue-words).
        `${issues.name(node.key)} · ${found.message}`,
        { nodeKey: node.key, field: found.field },
      );
    }
  }
}

function outbound(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  for (const found of options.outboundRefusals ?? []) {
    if (!index.nodes.has(found.nodeKey)) continue;
    issues.issue(
      strict,
      C.URL_NOT_ALLOWED,
      `${issues.name(found.nodeKey)} · ${issues.field(found.nodeKey, found.field)}: ${found.message}`,
      { nodeKey: found.nodeKey, field: found.field, details: found.details },
    );
  }
}

// A warning on the draft (the author is mid-edit), an error at publish (§5.1).
function codeFindings(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  for (const found of options.codeFindings ?? []) {
    if (!index.nodes.has(found.nodeKey)) continue;
    const where = found.line ? ` (line ${found.line})` : '';
    issues.issue(
      strict,
      C.CODE_INVALID,
      `${issues.name(found.nodeKey)} · Code${where}: ${found.message}`,
      {
        nodeKey: found.nodeKey,
        field: 'source',
        details: { line: found.line, column: found.column, runtimeCode: found.code },
      },
    );
  }
}

// A pinned version runs with the handler it was written for: `(request, context)` code handed
// an event (or the reverse) reads fields that are not there and returns the wrong shape.
function codeSignatures(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  if (!options.codeSignatures) return;
  for (const node of index.nodes.values()) {
    if (node.type !== ENodeType.CODE || typeof node.config?.source === 'string') continue;
    const versionId = node.config?.transformVersionId;
    const wanted = typeof versionId === 'string' ? options.codeSignatures.get(versionId) : null;
    const actual = node.config?.signature === 'request' ? 'request' : 'event';
    if (!wanted || wanted === actual) continue;
    const shape = wanted === 'request' ? '(request, context)' : '(event, ctx)';
    issues.issue(
      strict,
      C.CODE_SIGNATURE_MISMATCH,
      `${issues.name(node.key)} · the pinned version is written as ${shape}, so the step must run it that way (signature "${wanted}")`,
      { nodeKey: node.key, field: 'signature', details: { expected: wanted, actual } },
    );
  }
}

function promptFindings(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  for (const found of options.promptFindings ?? []) {
    if (!index.nodes.has(found.nodeKey)) continue;
    const at = { nodeKey: found.nodeKey, field: found.field };
    const message = `${issues.name(found.nodeKey)} · ${found.message}`;
    if (found.unbound) issues.issue(strict, C.PROMPT_VARIABLE_UNBOUND, message, at);
    else issues.warn(C.PROMPT_VARIABLE_UNKNOWN, message, at);
  }
}

function agentRules(
  index: IGraphIndex,
  issues: Collector,
  options: IValidateOptions,
  strict: boolean,
): void {
  const own = [...index.nodes.values()]
    .filter((node) => node.type === ENodeType.AI_AGENT)
    .flatMap((node) => agentFindings(node));
  for (const found of [...own, ...(options.agentFindings ?? [])]) {
    if (!index.nodes.has(found.nodeKey)) continue;
    const at = { nodeKey: found.nodeKey, field: found.field };
    const message = `${issues.name(found.nodeKey)} · ${found.message}`;
    if (found.level === 'error') issues.error(found.code, message, at);
    else if (found.level === 'warning') issues.warn(found.code, message, at);
    else issues.issue(strict, found.code, message, at);
  }
}

function reachability(index: IGraphIndex, issues: Collector): void {
  for (const scope of index.scopeMembers.keys()) {
    const entries = scopeEntries(index, scope);
    if (entries.length !== 1) continue;
    const reached = new Set([entries[0], ...descendants(index, entries[0])]);
    for (const key of index.scopeMembers.get(scope) ?? []) {
      if (reached.has(key)) continue;
      if (index.descriptors.get(key)?.kind === ENodeKind.TRIGGER) continue;
      issues.error(
        C.NODE_UNREACHABLE,
        scope === ROOT_SCOPE
          ? `${issues.name(key)} is not connected: no path from the trigger reaches it`
          : `${issues.name(key)} is not connected inside ${issues.name(scope)}`,
        { nodeKey: key },
      );
    }
  }
}

// An agent's finding inside `tools.2.…` names that tool: the panel lists several side by side.
function toolNameAt(config: Record<string, unknown>, field: string): string | null {
  const at = /^tools\.(\d+)\./.exec(field);
  const tools = config.tools;
  if (!at || !Array.isArray(tools)) return null;
  const tool: unknown = tools[Number(at[1])];
  const name = tool !== null && typeof tool === 'object' ? (tool as { name?: unknown }).name : null;
  return typeof name === 'string' && name !== '' ? name : null;
}

function nodeConfigs(index: IGraphIndex, issues: Collector): void {
  for (const [key, node] of index.nodes) {
    const descriptor = index.descriptors.get(key);
    if (!descriptor) continue;
    const config = node.config ?? {};
    const result = descriptor.configSchema.safeParse(config);
    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path.map(String).join('.');
        const value = valueAt(config, issue.path);
        const missing = issue.code === 'invalid_type' && value === undefined;
        const inCondition = /^(condition|while|routes\.\d+\.condition)/.test(field);
        const label =
          fieldLabel(descriptor.fields, field) ??
          (field === '' ? descriptor.fields.find((ui) => ui.required)?.label : undefined) ??
          fallbackLabel(field);
        const noField = inCondition && field.endsWith('.path') && (value ?? '') === '';
        const tool = node.type === ENodeType.AI_AGENT ? toolNameAt(config, field) : null;
        issues.error(
          missing
            ? C.NODE_CONFIG_REQUIRED
            : inCondition
              ? C.CONDITION_INVALID
              : C.NODE_CONFIG_INVALID,
          `${node.name} · ${noField ? 'Choose a field to check' : plainProblem(issue, value, label)}${tool ? ` · tool ${tool}` : ''}`,
          { nodeKey: key, field: field || descriptor.fields.find((ui) => ui.required)?.path },
        );
      }
    }
    if (node.retry && !descriptor.retries) {
      issues.error(C.RETRY_NOT_SUPPORTED, `${node.name} runs inline and has nothing to retry`, {
        nodeKey: key,
        field: 'retry',
      });
    }
  }
}

function disabledNodes(index: IGraphIndex, issues: Collector): void {
  for (const [key, node] of index.nodes) {
    const descriptor = index.descriptors.get(key);
    if (!node.disabled || !descriptor) continue;
    if (!descriptor.disableable) {
      issues.error(
        C.NODE_CANNOT_BE_DISABLED,
        `${node.name} cannot be skipped in runs: a ${descriptor.label} has no output to pass an event straight through`,
        { nodeKey: key, field: 'disabled' },
      );
      continue;
    }
    const main = (index.outputs.get(key) ?? []).find((spec) => spec.main)?.key;
    const starved = (index.out.get(key) ?? []).filter((edge) => edge.output !== main);
    if (starved.length === 0) continue;
    const names = [...new Set(starved.map((edge) => issues.name(edge.to)))].join(', ');
    issues.warn(
      C.DISABLED_SKIPS_BRANCH,
      `${node.name} is skipped in runs, so every event leaves by ${main}; ${names} never get one`,
      { nodeKey: key, details: { outputs: [...new Set(starved.map((e) => e.output))] } },
    );
  }
}

function loopBodies(index: IGraphIndex, issues: Collector): void {
  for (const [key, node] of index.nodes) {
    if (node.type !== ENodeType.LOOP) continue;
    const members = index.scopeMembers.get(key) ?? [];
    if (members.length === 0) {
      issues.error(C.LOOP_BODY_EMPTY, `${node.name} has nothing to repeat — put steps inside it`, {
        nodeKey: key,
        field: 'children',
      });
      continue;
    }
    const entries = scopeEntries(index, key);
    const exits = scopeExits(index, key);
    if (entries.length !== 1) {
      issues.error(
        C.LOOP_BODY_ENTRY,
        `${node.name} must start at one step; ${entries.map((k) => issues.name(k)).join(', ')} all start it`,
        { nodeKey: key, details: { entries } },
      );
    }
    if (exits.length !== 1) {
      issues.error(
        C.LOOP_BODY_EXIT,
        `${node.name} must end at one step; ${exits.map((k) => issues.name(k)).join(', ')} all end it`,
        { nodeKey: key, details: { exits } },
      );
    }
    if (entries.length !== 1 || exits.length !== 1) continue;
    const [entry, exit] = [entries[0], exits[0]];
    if (countPaths(index, entry, exit) <= 1) continue;
    const dominators = buildDominance(index).local.get(exit) ?? new Set<string>();
    const joined = [...dominators].some(
      (k) => index.nodes.get(k)?.type === ENodeType.MERGE && countPaths(index, k, exit) === 1,
    );
    if (!joined) {
      issues.error(
        C.LOOP_BODY_NEEDS_MERGE,
        `Several paths inside ${node.name} reach ${issues.name(exit)}; join them with a Merge so ` +
          'one event comes out of each round',
        { nodeKey: key },
      );
    }
  }
}

function merges(index: IGraphIndex, issues: Collector): void {
  for (const [key, node] of index.nodes) {
    if (node.type !== ENodeType.MERGE) continue;
    for (const input of index.inputs.get(key) ?? []) {
      if ((index.in.get(key) ?? []).some((edge) => edge.input === input)) continue;
      issues.error(
        C.MERGE_INPUT_UNCONNECTED,
        `${node.name} · input ${input} has nothing leading into it`,
        {
          nodeKey: key,
          field: `inputs.${input}`,
        },
      );
    }
  }
}

function placement(index: IGraphIndex, dominance: IDominance, issues: Collector): void {
  const collected = new Set<string>();
  for (const [key, node] of index.nodes) {
    if (node.type === ENodeType.DEDUPE) {
      const above = new Set<string>(ancestors(index, key));
      for (const loop of enclosingLoops(index, key)) {
        above.add(loop);
        for (const k of ancestors(index, loop)) above.add(k);
      }
      const split = [...above].find((k) => index.nodes.get(k)?.type === ENodeType.SPLIT);
      if (split) {
        issues.error(
          C.DEDUPE_BELOW_SPLIT,
          `${node.name} runs once per request, so it goes above ${issues.name(split)}, not below it`,
          { nodeKey: key },
        );
      }
    }
    if (node.type !== ENodeType.AGGREGATE) continue;
    const split = pairedSplit(index, dominance, key);
    if (!split) {
      const splits = dominance
        .chain(key)
        .filter((k) => index.nodes.get(k)?.type === ENodeType.SPLIT);
      issues.error(
        C.AGGREGATE_WITHOUT_SPLIT,
        splits.length > 0
          ? `${node.name} has nothing to collect: every Split above it (${splits.map((k) => issues.name(k)).join(', ')}) is already collected by another Aggregate`
          : `${node.name} collects what a Split made, and every path to it must come through one`,
        { nodeKey: key },
      );
    } else if (scopeOf(index, split) !== scopeOf(index, key)) {
      issues.error(
        C.AGGREGATE_WITHOUT_SPLIT,
        `${node.name} is inside ${issues.name(scopeOf(index, key))}, but the Split it would collect, ` +
          `${issues.name(split)}, is outside it; put both on the same side of the loop frame`,
        { nodeKey: key, details: { split } },
      );
    } else {
      collected.add(split);
    }
  }
  for (const [key, node] of index.nodes) {
    const loop = index.parentLoop.get(key);
    if (node.type !== ENodeType.SPLIT || loop === undefined || collected.has(key)) continue;
    issues.error(
      C.SPLIT_LEAVES_LOOP,
      `${node.name} makes a copy per item inside ${issues.name(loop)}, but one copy leaves each ` +
        `round: collect them with an Aggregate inside ${issues.name(loop)}`,
      { nodeKey: key },
    );
  }
}

type TExplained = Map<string, { code: TFlowsErrorCode; message: string }>;

function references(
  index: IGraphIndex,
  dominance: IDominance,
  issues: Collector,
  strict: boolean,
): void {
  const explained: TExplained = new Map();
  for (const [key, node] of index.nodes) {
    const descriptor = index.descriptors.get(key);
    if (!descriptor) continue;
    const found: { field: string; path: IContextPath }[] = [];
    const { refs, problems } = collectTemplateRefs(
      node.config ?? {},
      descriptor.templateFields,
      descriptor.templateRoots,
    );
    for (const problem of problems) {
      issues.issue(
        strict,
        C.TEMPLATE_INVALID,
        `${node.name} · ${issues.field(key, problem.field)}: ${problem.message}`,
        { nodeKey: key, field: problem.field },
      );
    }
    for (const ref of refs) found.push({ field: ref.field, path: ref });
    for (const spec of descriptor.contextPaths(node.config ?? {})) {
      try {
        found.push({ field: spec.field, path: parseContextPath(spec.path) });
      } catch (error) {
        const inCondition = spec.field.includes('condition') || spec.field.startsWith('while');
        // At publish the schema already said "Choose a field to check" for an empty path.
        if (strict && inCondition && !spec.path.trim()) continue;
        const message = !spec.path.trim()
          ? inCondition
            ? 'Choose a field to check'
            : `Choose a field for ${issues.field(key, spec.field)}`
          : `${issues.field(key, spec.field)}: ${error instanceof Error ? error.message : String(error)}`;
        issues.issue(
          strict,
          inCondition ? C.CONDITION_INVALID : C.TEMPLATE_INVALID,
          `${node.name} · ${message}`,
          { nodeKey: key, field: spec.field },
        );
      }
    }
    for (const { field, path } of found) {
      if (strict) {
        checkPath(index, dominance, issues, explained, key, field, path);
      } else if (path.nodeKey && !index.nodes.has(path.nodeKey)) {
        issues.warn(
          C.TEMPLATE_MISSING_NODE,
          removedMessage(issues, node.name, key, field, path.nodeKey),
          { nodeKey: key, field, details: { path: path.path, refersTo: path.nodeKey } },
        );
      }
    }
  }
}

function checkPath(
  index: IGraphIndex,
  dominance: IDominance,
  issues: Collector,
  explained: TExplained,
  key: string,
  field: string,
  path: IContextPath,
): void {
  const node = index.nodes.get(key)!;
  const ownWhile = node.type === ENodeType.LOOP && field.startsWith('while');
  const visible = new Set(dominance.visible(key));
  if (ownWhile) {
    // `while` is checked after a round, so it may read what the body's last step saw.
    const exits = scopeExits(index, key).map(
      (exit) => dominance.local.get(exit) ?? new Set<string>(),
    );
    for (const k of exits[0] ?? []) if (exits.every((set) => set.has(k))) visible.add(k);
  }
  const brackets = splitBrackets(index, dominance, key);
  const at = { nodeKey: key, field, details: { path: path.path } };

  if (path.root === 'item') {
    if (brackets.open) return;
    const last = brackets.closed[0];
    if (last) {
      issues.error(
        C.TEMPLATE_AFTER_AGGREGATE,
        `${node.name} · ${path.path}: item.* is one item of ${issues.name(last.split)}; after ` +
          `${issues.name(last.aggregate)} only the collected list exists`,
        at,
      );
    } else {
      issues.error(
        C.TEMPLATE_ITEM_OUTSIDE_SPLIT,
        `${node.name} · ${path.path}: there is no Split above this step`,
        at,
      );
    }
    return;
  }
  if (path.root === 'loop') {
    if (!ownWhile && enclosingLoops(index, key).length === 0) {
      issues.error(
        C.TEMPLATE_LOOP_OUTSIDE_LOOP,
        `${node.name} · ${path.path}: this step is not inside a Loop`,
        at,
      );
    }
    return;
  }
  if (path.root !== 'nodes' || !path.nodeKey) return;
  const target = path.nodeKey;
  const refAt = { ...at, details: { path: path.path, refersTo: target } };
  if (!visible.has(target)) {
    const problem =
      ownWhile && scopeOf(index, target) === key
        ? {
            code: C.TEMPLATE_NOT_ON_EVERY_PATH,
            message:
              `${issues.name(target)} runs on only some paths through ${node.name}; the ` +
              'condition is checked after every round, so it reads only steps every round passes',
          }
        : explainCached(index, dominance, key, target, issues, explained);
    issues.error(
      problem.code,
      referenceMessage(issues, node.name, key, field, target, problem),
      refAt,
    );
    return;
  }
  for (const { split, aggregate } of brackets.closed) {
    if (target === split || !dominance.visible(aggregate).has(target)) continue;
    if (!dominance.visible(target).has(split)) continue;
    issues.error(
      C.TEMPLATE_AFTER_AGGREGATE,
      `${node.name} · ${issues.field(key, field)}: ${issues.name(target)} runs once per item; after ` +
        `${issues.name(aggregate)} only the collected list exists`,
      refAt,
    );
    return;
  }
  if (index.nodes.get(target)?.disabled) {
    issues.warn(
      C.TEMPLATE_DISABLED_NODE,
      `${node.name} · ${issues.field(key, field)}: ${issues.name(target)} is skipped in runs, so this value is empty`,
      refAt,
    );
  }
}

function explainCached(
  index: IGraphIndex,
  dominance: IDominance,
  reader: string,
  target: string,
  names: { name(key: string): string },
  explained: TExplained,
): { code: TFlowsErrorCode; message: string } {
  const id = `${reader}\u0000${target}`;
  let problem = explained.get(id);
  if (!problem) {
    problem = explainReference(index, dominance, reader, target, names);
    explained.set(id, problem);
  }
  return problem;
}

/** Why `target`'s output is not known at `reader` — the data-picker texts (§5.3). */
export function explainReference(
  index: IGraphIndex,
  dominance: IDominance,
  reader: string,
  target: string,
  names: { name(key: string): string },
): { code: TFlowsErrorCode; message: string } {
  if (!index.nodes.has(target)) {
    return {
      code: C.TEMPLATE_MISSING_NODE,
      message: `${names.name(target)} was removed from the flow`,
    };
  }
  if (target === reader) {
    return { code: C.TEMPLATE_SELF_REFERENCE, message: 'a step cannot read its own answer' };
  }
  const scope = scopeOf(index, target);
  const lifted = [reader, ...enclosingLoops(index, reader)].find(
    (k) => scopeOf(index, k) === scope,
  );
  if (lifted === undefined) {
    return {
      code: C.TEMPLATE_INSIDE_LOOP,
      message: `${names.name(target)} is inside the loop ${names.name(scope)}; steps outside it cannot read it`,
    };
  }
  if (lifted === target) {
    return {
      code: C.TEMPLATE_SELF_REFERENCE,
      message: `this step repeats inside ${names.name(target)}, which has no answer until it finishes`,
    };
  }
  const here = names.name(lifted);
  if (descendants(index, lifted).has(target)) {
    return {
      code: C.TEMPLATE_DOWNSTREAM,
      message: `${names.name(target)} is further down. Nothing below ${here} has run when ${here} runs.`,
    };
  }
  if (descendants(index, target).has(lifted)) {
    const merge = [lifted, ...dominance.chain(lifted)].find(
      (k) => index.nodes.get(k)?.type === ENodeType.MERGE && ancestors(index, k).has(target),
    );
    if (merge) {
      const name = names.name(merge);
      return {
        code: C.TEMPLATE_NOT_ON_EVERY_PATH,
        message: !mergeWaitsForAll(index.nodes.get(merge))
          ? `${names.name(target)} is on one input of ${name}, which goes on with whichever input ` +
            `arrives first — set ${name} to wait for every input to use it.`
          : `${names.name(target)} is on a way into ${name} that an event can leave before it ` +
            `arrives (a Filter, a Route branch or another way out), so ${here} cannot count on it.`,
      };
    }
    const fork = fanoutFork(index, dominance, lifted, target);
    return {
      code: C.TEMPLATE_NOT_ON_EVERY_PATH,
      message: fork
        ? `${names.name(target)} comes from only one branch of the fan-out at ${names.name(fork)}; ` +
          `${here} runs after the branches join, so it cannot count on it.`
        : `${names.name(target)} is not on every path here — an event can reach ${here} without going through it.`,
    };
  }
  const split = divergence(index, dominance, target, lifted);
  if (split?.sameOutput) {
    return split.direct
      ? {
          code: C.TEMPLATE_PARALLEL_BRANCH,
          message: `${names.name(target)} runs next to ${here}, not before it — its answer is not known yet.`,
        }
      : {
          code: C.TEMPLATE_DOWNSTREAM,
          message: `${names.name(target)} is further down. Nothing below ${here} has run when ${here} runs.`,
        };
  }
  const branch = split?.targetOutput;
  return {
    code: C.TEMPLATE_OTHER_BRANCH,
    message: branch
      ? `${names.name(target)} is on the ${branch} branch. An event that reaches ${here} never went through it.`
      : `${names.name(target)} is on another branch. An event that reaches ${here} never went through it.`,
  };
}

/** The nearest dominator of `reader` above `target` when it is an edge fan-out toward it. */
function fanoutFork(
  index: IGraphIndex,
  dominance: IDominance,
  reader: string,
  target: string,
): string | null {
  const leads = (edge: TFlowEdge) => edge.to === target || descendants(index, edge.to).has(target);
  for (const fork of dominance.chain(reader)) {
    if (!descendants(index, fork).has(target)) continue;
    const ports = new Map<string, TFlowEdge[]>();
    for (const edge of index.out.get(fork) ?? []) {
      ports.set(edge.output, [...(ports.get(edge.output) ?? []), edge]);
    }
    return [...ports.values()].some((port) => port.length > 1 && port.some(leads)) ? fork : null;
  }
  return null;
}

function divergence(
  index: IGraphIndex,
  dominance: IDominance,
  target: string,
  reader: string,
): { sameOutput: boolean; direct: boolean; targetOutput?: string } | null {
  const a = dominance.local.get(target);
  const b = dominance.local.get(reader);
  if (!a || !b) return null;
  const order = topoOrder(index, scopeOf(index, target)).order;
  const common = order.filter((key) => a.has(key) && b.has(key));
  const fork = common[common.length - 1];
  if (!fork) return null;
  const leads = (edge: TFlowEdge, key: string) =>
    edge.to === key || descendants(index, edge.to).has(key);
  const out = index.out.get(fork) ?? [];
  for (const e1 of out) {
    if (!leads(e1, target)) continue;
    const e2 = out.find((edge) => edge !== e1 && edge.output === e1.output && leads(edge, reader));
    if (e2) return { sameOutput: true, direct: e1.to === target };
  }
  return {
    sameOutput: false,
    direct: false,
    targetOutput: out.find((e) => leads(e, target))?.output,
  };
}

const CATCHABLE: readonly ENodeKind[] = [
  ENodeKind.SYNC,
  ENodeKind.ASYNC,
  ENodeKind.JOIN,
  ENodeKind.CONTAINER,
];

function warnings(index: IGraphIndex, issues: Collector, options: IPublishOptions): void {
  const ports = fanoutPorts(index);
  for (const [key, node] of index.nodes) {
    if (node.type === ENodeType.MERGE && (node.config?.mode ?? 'all') === 'all') {
      const reach = joinInputReach(index, key);
      const inputs = index.inputs.get(key) ?? [];
      for (let i = 0; i < inputs.length; i++) {
        for (let j = i + 1; j < inputs.length; j++) {
          if (!reach.get(inputs[i])?.size || !reach.get(inputs[j])?.size) continue;
          if (inputsCanCoOccur(ports, key, inputs[i], inputs[j], reach)) continue;
          issues.warn(
            C.MERGE_INPUTS_EXCLUSIVE,
            `${node.name}: inputs ${inputs[i]} and ${inputs[j]} never both arrive in one run, so ` +
              'waiting for every input is the same as going on with the first one',
            { nodeKey: key, details: { inputs: [inputs[i], inputs[j]] } },
          );
        }
      }
    }
    const descriptor = index.descriptors.get(key);
    const hasError = (index.outputs.get(key) ?? []).some((spec) => spec.key === ERROR_PORT);
    const handled = (index.out.get(key) ?? []).some((edge) => edge.output === ERROR_PORT);
    const retries = descriptor?.retries
      ? (node.retry?.maxAttempts ?? descriptor.defaultRetry?.maxAttempts ?? 2) > 1
      : false;
    // In a loop body an unhandled error leaves by the loop's own error output (§5.4).
    const inBody = index.parentLoop.has(key);
    const catchable = !!descriptor && CATCHABLE.includes(descriptor.kind);
    if (hasError && !handled && !retries && catchable && !inBody && !node.disabled) {
      issues.warn(
        C.ERROR_UNHANDLED_WITHOUT_RETRY,
        `${node.name} does not retry and nothing catches its error output, so every failure opens an issue`,
        { nodeKey: key },
      );
    }
  }
  for (const key of options.silentNodeKeys ?? []) {
    if (!index.nodes.has(key)) continue;
    issues.warn(
      C.NODE_WITHOUT_TRAFFIC,
      `No event has reached ${issues.name(key)} in the last 7 days`,
      {
        nodeKey: key,
      },
    );
  }
}

export type TCanConnect = { ok: true } | { ok: false; issue: IFlowIssue };

/** The canvas asks this while an edge is dragged, to light the inputs it may drop on. */
export function canConnect(
  graph: TFlowGraph,
  from: string,
  output: string,
  to: string,
  input: string,
): TCanConnect {
  const edge = { from, output, to, input };
  const id = edgeId(edge);
  if (graph.edges.some((existing) => edgeId(existing) === id)) {
    return {
      ok: false,
      issue: { code: C.EDGE_DUPLICATE, severity: 'error', message: 'Already connected', edge },
    };
  }
  const candidate: TFlowGraph = { ...graph, edges: [...graph.edges, edge] };
  const issues = new Collector(candidate);
  const withEdge = buildGraphIndex(candidate);
  triggers(withEdge, issues);
  edges(withEdge, issues);
  const own = issues.finish().find((issue) => issue.edge && edgeId(issue.edge) === id);
  if (own) return { ok: false, issue: own };

  const before = buildGraphIndex(graph);
  if (descendants(before, to).has(from)) {
    return {
      ok: false,
      issue: {
        code: C.EDGE_CREATES_CYCLE,
        severity: 'error',
        message:
          `Joining ${issues.name(from)} back to ${issues.name(to)} would send every event round ` +
          'forever. To repeat part of a flow, wrap it in a Loop — it has a condition and a limit.',
        edge,
        nodeKey: from,
      },
    };
  }
  return { ok: true };
}

function valueAt(value: unknown, path: readonly PropertyKey[]): unknown {
  let cursor: unknown = value;
  for (const segment of path) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<PropertyKey, unknown>)[segment];
  }
  return cursor;
}

function removedMessage(
  issues: Collector,
  reader: string,
  readerKey: string,
  field: string,
  target: string,
): string {
  const what = issues.wasRemoved(target)
    ? `a step that was removed: ${issues.name(target)}`
    : `a step that doesn't exist: ${target}`;
  return `${reader} · ${issues.field(readerKey, field)} uses ${what}`;
}

function referenceMessage(
  issues: Collector,
  reader: string,
  readerKey: string,
  field: string,
  target: string,
  problem: { code: TFlowsErrorCode; message: string },
): string {
  if (problem.code === C.TEMPLATE_MISSING_NODE) {
    return removedMessage(issues, reader, readerKey, field, target);
  }
  return `${reader} · ${issues.field(readerKey, field)}: ${problem.message}`;
}
